// Collapsed-pill tool-call display. Click to expand inline and inspect
// the full args + result data. Mirrors the codeBlock collapse pattern
// but rounder + smaller.
//
// Icons are naked stroke SVGs (Lucide-family geometry) — no emoji,
// no monospace anywhere. Body uses Inter; only inline code-like
// fragments get a faint mono fallback.

import { useEffect, useRef, useState } from "react";
import type { ToolResult } from "../lib/types";
import { copyTextToClipboard } from "../lib/clipboard";

/** Format ms-elapsed into a short human string: "12s", "1m 23s", "5m". */
function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

interface Props {
  toolName: string;
  args: Record<string, unknown>;
  status: "running" | "ok" | "error";
  result?: ToolResult;
  /** Parent turn liveness only; stored status/results remain unchanged. */
  active?: boolean;
  /** Consolidated technical disclosure: preserve status, skip duplicate animation/timers. */
  quiet?: boolean;
}

type IconKind =
  | "file"
  | "pencil"
  | "search"
  | "folder"
  | "terminal"
  | "globe"
  | "sparkles"
  | "bot"
  | "arrowDown"
  | "arrowUp"
  | "wrench"
  | "trash"
  | "tool";

const KIND_FOR_TOOL: Record<string, IconKind> = {
  // Filesystem
  Read: "file",
  Write: "pencil",
  Edit: "pencil",
  Glob: "search",
  Grep: "search",
  LS: "folder",
  // Execution
  Bash: "terminal",
  // Web
  WebFetch: "globe",
  WebSearch: "search",
  // Skills (slash commands)
  Skill: "sparkles",
  Task: "bot",
  // Reputifly-specific (Archimedes ops)
  ArchimedesGet: "arrowDown",
  ArchimedesPost: "arrowUp",
  ArchimedesPatch: "wrench",
  ArchimedesDelete: "trash",
  // Legacy local-AI labels (back-compat)
  list_dir: "folder",
  read_file: "file",
  find_files: "search",
  grep: "search",
};

function ToolIcon({ kind }: { kind: IconKind }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (kind) {
    case "file":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case "pencil":
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case "folder":
      return (
        <svg {...common}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "terminal":
      return (
        <svg {...common}>
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      );
    case "globe":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" />
        </svg>
      );
    case "sparkles":
      return (
        <svg {...common}>
          <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4L12 3z" />
          <path d="M18.5 14.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
        </svg>
      );
    case "bot":
      return (
        <svg {...common}>
          <rect x="3" y="8" width="18" height="12" rx="3" />
          <line x1="12" y1="4" x2="12" y2="8" />
          <circle cx="8.5" cy="14" r="1.1" />
          <circle cx="15.5" cy="14" r="1.1" />
        </svg>
      );
    case "arrowDown":
      return (
        <svg {...common}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <polyline points="6 13 12 19 18 13" />
        </svg>
      );
    case "arrowUp":
      return (
        <svg {...common}>
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="6 11 12 5 18 11" />
        </svg>
      );
    case "wrench":
      return (
        <svg {...common}>
          <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z" />
        </svg>
      );
    case "trash":
      return (
        <svg {...common}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
      );
    case "tool":
    default:
      return (
        <svg {...common}>
          <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z" />
        </svg>
      );
  }
}

function formatArgsInline(name: string, args: Record<string, unknown>): string {
  // First arg, truncated. Most claude tools have a primary path/cmd/query.
  const primary =
    args.path ?? args.command ?? args.pattern ?? args.url ?? args.file_path ?? args.glob;
  if (primary != null) {
    const s = String(primary);
    const trimmed = s.length > 60 ? s.slice(0, 57) + "…" : s;
    return `${name}(${trimmed})`;
  }
  return `${name}(…)`;
}

// Fixed progress labels intentionally withhold arguments/output. Do not offer
// an empty disclosure, and never infer completion from another card's title.
function hasDetail(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

const CODEX_PROGRESS_ICONS: Record<string, IconKind> = {
  "Codex shell task": "terminal", "Codex Build command": "terminal",
  "Codex file changes": "pencil", "Codex Build file change": "pencil",
  "Codex web research": "globe", "Codex Build web search": "globe",
  "Codex delegated task": "bot", "Codex Build agent": "bot",
  "Codex connected tool": "tool", "Codex Build tool": "tool",
  "Codex image task": "sparkles",
  "Codex image review": "file",
  "Codex lane queue": "bot", "Codex Lane3 queue": "bot",
};

export default function ToolBubble({ toolName, args, status, result, active = true, quiet = false }: Props) {
  const running = status === "running" && active;
  const inactive = status === "running" && !active;
  const [expanded, setExpanded] = useState(false);
  // Elapsed-time tracker: captures the moment we first see status="running"
  // and re-renders every second so the user knows IF claude is still alive
  // and HOW LONG this tool has been working. Without this the user can't
  // distinguish "claude is genuinely thinking for 60s" from "stuck forever".
  // Critical for Julian-on-phone-during-NS: a misleading stale chip is the
  // #1 reason for premature Stop clicks + double-sends.
  const startedAtRef = useRef<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (running && !quiet) {
      if (startedAtRef.current === null) startedAtRef.current = Date.now();
      const id = window.setInterval(() => setNow(Date.now()), 1000);
      return () => window.clearInterval(id);
    }
    // status changed away from running — freeze the elapsed display.
    return;
  }, [running, quiet]);
  const elapsedMs = startedAtRef.current != null ? Math.max(0, now - startedAtRef.current) : 0;
  const showElapsed = running && !quiet && elapsedMs >= 5_000;
  const progressOnly = toolName.startsWith("Codex ") && !hasDetail(args)
    && !hasDetail(result?.data) && !hasDetail(result?.error);
  const summary =
    inactive ? "Inactive" : running
      ? quiet ? "In progress" : showElapsed
        ? `running… ${formatElapsed(elapsedMs)}`
        : "running…"
      : progressOnly ? (status === "ok" ? "Completed" : "Failed")
        : result?.summary ?? (status === "ok" ? "ok" : "error");
  const isErr = status === "error";
  const kind = KIND_FOR_TOOL[toolName] ?? CODEX_PROGRESS_ICONS[toolName] ?? "tool";
  const Header = progressOnly ? "div" : "button";

  return (
    <div
      className={`ag__toolBubble${progressOnly ? " is-progress-only" : ""}${expanded && !progressOnly ? " is-expanded" : ""}${
        isErr ? " is-error" : ""
      }${status === "ok" ? " is-ok" : ""}${running && !quiet ? " is-running" : ""}`}
    >
      <Header
        type={progressOnly ? undefined : "button"}
        role={progressOnly ? "status" : undefined}
        className="ag__toolBubbleHeader"
        onClick={progressOnly ? undefined : () => setExpanded((e) => !e)}
        aria-expanded={progressOnly ? undefined : expanded}
        title={inactive ? "This task is no longer active; no completion event was recorded for this tool." : progressOnly ? result?.summary : undefined}
      >
        <span className="ag__toolBubbleIcon" aria-hidden="true">
          <ToolIcon kind={kind} />
        </span>
        <span className="ag__toolBubbleName">
          {progressOnly ? toolName : formatArgsInline(toolName, args)}
        </span>
        <span className="ag__toolBubbleStatus">
          {inactive ? (
            <span aria-hidden="true">—</span>
          ) : running && quiet ? (
            <span aria-hidden="true">·</span>
          ) : running ? (
            <span className="ag__toolSpinner" aria-hidden="true" />
          ) : status === "ok" ? (
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          )}
          <span className="ag__toolBubbleSummary">{summary}</span>
        </span>
        {!progressOnly && <span className="ag__toolBubbleChevron" aria-hidden="true">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>}
      </Header>
      {expanded && !progressOnly && (
        <div className="ag__toolBubbleBody">
          <ToolSection label="arguments" text={JSON.stringify(args, null, 2)} />
          <ToolSection
            label={isErr ? "error" : "result"}
            text={
              isErr
                ? result?.error ?? "unknown error"
                : JSON.stringify(result?.data ?? null, null, 2)
            }
          />
        </div>
      )}
    </div>
  );
}

/* Every box the operator can see, he can copy. A tool's arguments or its error
 * text is exactly the thing worth pasting somewhere -- into a bug report, into
 * a message to whoever owns the failing endpoint -- and until 2026-08-25 these
 * two were the only boxes in the app with no way to get the text out.
 *
 * The label doubles as the copy control rather than adding a second row: the
 * section header was already there and already the right width. */
function ToolSection({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  function copy() {
    void copyTextToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1400);
    });
  }

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  return (
    <div className="ag__toolBubbleSection">
      <div className="ag__toolBubbleSectionHead">
        <span className="ag__toolBubbleSectionLabel">{label}</span>
        <button
          type="button"
          className="ag__toolBubbleCopy"
          onClick={copy}
          aria-label={`Copy ${label}`}
          title={copied ? "Copied" : "Copy"}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="ag__toolBubblePre">{text}</pre>
    </div>
  );
}
